<template>
  <div class="dashboard-container">
    <ModalLoading :isLoading="isLoading" />

    <!-- Tarjetas informativas con iconos de Bootstrap y enlaces "Ver más" -->
    <div class="cards-container">
      <div class="card" v-for="(item, index) in cardsData" :key="index">
        <div class="card-content">
          <!-- Ventas Hoy con comparativo ayer -->
          <div v-if="item.customTemplate === 'comparativo'" class="value-content">
            <h3>{{ item.title }}</h3>
            <i class="bi bi-cash card-icon"></i>
            <p class="item-value">L. {{ ventasComparativo ? Number(ventasComparativo.hoy).toLocaleString('es-HN', {minimumFractionDigits:2,maximumFractionDigits:2}) : retornarVentas }}</p>
            <div v-if="ventasComparativo && ventasComparativo.cambio_pct !== null" class="trend-badge" :class="ventasComparativo.cambio_pct >= 0 ? 'trend-up' : 'trend-down'">
              <i :class="ventasComparativo.cambio_pct >= 0 ? 'bi bi-arrow-up-short' : 'bi bi-arrow-down-short'"></i>
              {{ Math.abs(ventasComparativo.cambio_pct) }}% vs ayer
            </div>
          </div>
          <!-- Ventas del Mes -->
          <div v-else-if="item.customTemplate === 'ventasMes'" class="value-content">
            <h3>{{ item.title }}</h3>
            <i class="bi bi-graph-up-arrow card-icon"></i>
            <p class="item-value">L. {{ Number(ventasMes.mes_actual).toLocaleString('es-HN', {minimumFractionDigits:2,maximumFractionDigits:2}) }}</p>
            <div v-if="ventasMes.cambio_pct !== null" class="trend-badge" :class="ventasMes.cambio_pct >= 0 ? 'trend-up' : 'trend-down'">
              <i :class="ventasMes.cambio_pct >= 0 ? 'bi bi-arrow-up-short' : 'bi bi-arrow-down-short'"></i>
              {{ Math.abs(ventasMes.cambio_pct) }}% vs mes ant.
            </div>
          </div>
          <!-- CxC Pendiente -->
          <div v-else-if="item.customTemplate === 'cxc'" class="value-content">
            <h3>{{ item.title }}</h3>
            <i class="bi bi-receipt-cutoff card-icon"></i>
            <p class="item-value">L. {{ Number(cxcSucursal.total_pendiente).toLocaleString('es-HN', {minimumFractionDigits:2,maximumFractionDigits:2}) }}</p>
            <small class="cxc-sub">{{ cxcSucursal.cantidad }} cliente{{ cxcSucursal.cantidad !== 1 ? 's' : '' }} con saldo</small>
          </div>
          <!-- Estado de Caja -->
          <div v-else-if="item.customTemplate === 'caja'" class="value-content">
            <h3>{{ item.title }}</h3>
            <i class="bi bi-cash-stack card-icon"></i>
            <div class="caja-badge" :class="cajaBadge.cls">{{ cajaBadge.label }}</div>
            <p class="caja-monto" v-if="cajaEstado">
              {{ cajaEstado.cajas_abiertas || 0 }}
              {{ cajaEstado.cajas_abiertas === 1 ? 'caja abierta' : 'cajas abiertas' }}
            </p>
          </div>
          <!-- Alertas inventario -->
          <div v-else-if="item.customTemplate === true">
            <h3>{{ item.title }}</h3>
            <div class="alert-boxes">
              <div class="alert-box normal">
                <span>{{ alertasNormales }}</span>
                <small>Normal</small>
              </div>
              <div class="alert-box warning">
                <span>{{ alertasAtencion }}</span>
                <small>Atención</small>
              </div>
              <div class="alert-box urgent">
                <span>{{ alertasUrgentes }}</span>
                <small>Urgente</small>
              </div>
            </div>
            <div v-if="stockCritico > 0" class="stock-critico-badge">
              <i class="bi bi-exclamation-triangle-fill"></i> {{ stockCritico }} producto{{ stockCritico !== 1 ? 's' : '' }} en stock crítico
            </div>
          </div>
          <!-- Card normal con valor e icono -->
          <div v-else class="value-content">
            <h3>{{ item.title }}</h3>
            <i :class="item.icon" class="card-icon"></i>
            <p class="item-value">{{ item.value }}</p>
          </div>
        </div>
        <a :href="item.link" class="card-link">Ver más</a>
      </div>
    </div>

    <div class="graphics-container">
      <!-- Gráfico de ventas + Top Productos -->
      <div class="charts-container">
        <div class="line-chart">
          <LineChart :chart-data="lineChartData" :chart-options="lineChartOptions" :styles="chartStyles" />
        </div>
        <!-- Top Productos General (reemplaza pie chart) -->
        <div class="top-panel">
          <div class="top-panel-header">
            <i class="bi bi-trophy-fill"></i> Top Productos
            <span class="top-col-label">vendidas</span>
          </div>
          <div v-if="topGeneral.length > 0" class="top-panel-list">
            <div v-for="(p, i) in topGeneral" :key="i" class="top-panel-item">
              <span class="top-rank" :class="{ 'rank-gold': i===0, 'rank-silver': i===1, 'rank-bronze': i===2 }">#{{ i + 1 }}</span>
              <span class="top-nombre">{{ p.nombre }}</span>
              <span class="top-unidades">{{ p.unidades }} uds</span>
            </div>
          </div>
          <div v-else class="no-data">
            <i class="bi bi-box-seam"></i>
            <p>Sin ventas registradas</p>
          </div>
        </div>
      </div>

      <!-- Tabla de últimas ventas -->
      <div class="sales-table-wrap">
      <table class="sales-table">
        <thead>
          <tr>
            <th>Nombre</th>
            <th>Vendedor</th>
            <th>Número</th>
            <th>Fecha</th>
            <th>Total</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(sale, index) in sales" :key="index">
            <td>{{ sale.nombre }}</td>
            <td>{{ sale.vendedor }}</td>
            <td>{{ sale.numero }}</td>
            <td>{{ sale.fecha }}</td>
            <td>{{ sale.total }}</td>
            <td>
              <router-link to="/administrar-ventas" class="card-link"
                >Ver más</router-link
              >
              <!-- <span>|</span>
              <button style="font-size: 1rem;">Descargar</button> -->
            </td>
          </tr>
        </tbody>
      </table>
      </div>
    </div>
  </div>
</template>

<script>
import { Line } from "vue-chartjs";
const { getTotalVentasDia } = require("../../services/dashboardSolicitudes");
const {
  getClientesPorEmpresa,
} = require("../../services/dashboardSolicitudes");
const {
  getAlertasPorPromocion,
} = require("../../services/dashboardSolicitudes");
const {
  getAlertasPorPromocionProducto,
} = require("../../services/dashboardSolicitudes");
import { notis } from "../../services/notificaciones.js";
const {
  getVentasUltimosTresMeses,
} = require("../../services/dashboardSolicitudes");
const { getUltimasVentas } = require("../../services/dashboardSolicitudes");
const { getVentasComparativo, getCajaEstado, getStockCritico, getTopProductos } = require("../../services/dashboardSolicitudes");
const { getVentasMes, getTopProductosGeneralSucursal, getTotalCxCSucursal } = require("../../services/dashboardSolicitudes");
import solicitudes from "../../services/solicitudes.js";
import ModalLoading from "@/components/ModalLoading.vue";
import { getAlerts } from "../../services/alertasSolicitudes.js";

import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
} from "chart.js";

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale
);

export default {
  name: "DashboardList",

  components: {
    LineChart: Line,
    ModalLoading,
  },

  data() {
    return {
      ventas: 0,
      clientesNumero: 0,
      alertasPorPromocion: 0,
      alertasPorPromocionProducto: 0,
      alertasUrgentes: 0,
      alertasAtencion: 0,
      alertasNormales: 0,
      ventasComparativo: null,
      cajaEstado: null,
      stockCritico: 0,
      topProductos: [],
      ventasMes: { mes_actual: 0, mes_anterior: 0, cambio_pct: null },
      topGeneral: [],
      cxcSucursal: { total_pendiente: 0, cantidad: 0 },
      refreshTimer: null,
      id_usuario: "",
      isLoading: false,
      chartStyles: {
        height: "100%",
        width: "100%",
        position: "relative",
      },
      lineChartData: {
        labels: [],
        datasets: [
          {
            label: "Ventas Pagadas",
            borderColor: "#16A34A",
            data: [],
            fill: false,
          },
          {
            label: "Ventas Canceladas",
            borderColor: "#DC2626",
            data: [],
            fill: false,
          },
        ],
      },
      lineChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: "top",
          },
          tooltip: {
            callbacks: {
              label: function (context) {
                const label = context.dataset.label || "";
                return `${label}: ${context.raw}`;
              },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              stepSize: 1,
              callback: function (value) {
                return Math.floor(value);
              },
            },
          },
        },
      },
      sales: [],
    };
  },

  computed: {
    retornarVentas() {
      return this.ventas;
    },
    cajaBadge() {
      if (!this.cajaEstado) return { label: 'Sin datos', cls: 'badge-grey' };
      if (this.cajaEstado.abierta) return { label: 'Abierta', cls: 'badge-green' };
      return { label: 'Cerrada', cls: 'badge-red' };
    },
    cardsData() {
      return [
        {
          title: "Ventas Hoy",
          customTemplate: 'comparativo',
          link: "/administrar-ventas",
        },
        {
          title: "Ventas del Mes",
          customTemplate: 'ventasMes',
          link: "/administrar-ventas",
        },
        {
          title: "Caja",
          customTemplate: 'caja',
          link: "/arqueo-caja",
        },
        {
          title: "Cuentas por Cobrar",
          customTemplate: 'cxc',
          link: "/cuentas-por-cobrar",
        },
        {
          title: "Alertas",
          customTemplate: true,
          link: "/admin-invenario",
        },
      ];
    },
  },

  methods: {
    async obtenerDetalleAlertas() {
      const rol = Number(localStorage.getItem('role') || 0);
      if (rol === 3) return; // cajero no tiene alertas
      try {
        const alertas = await getAlerts(this.id_usuario);
        // Clasificar las alertas según su puntaje
        this.alertasUrgentes = alertas.filter(
          (alerta) => alerta.puntaje >= 80
        ).length;
        this.alertasAtencion = alertas.filter(
          (alerta) => alerta.puntaje >= 45 && alerta.puntaje < 80
        ).length;
        this.alertasNormales = alertas.filter(
          (alerta) => alerta.puntaje < 45
        ).length;
      } catch (error) {
        console.error("Error al obtener detalles de alertas:", error);
        if (!error?.message?.includes('401')) notis("error", "Error al obtener las alertas");
      }
    },

    async devolverVenta() {
      try {
        this.ventas = await getTotalVentasDia(this.id_usuario);
      } catch (error) {
        if (!error?.message?.includes('401')) notis("error", "Error al buscar ventas");
      }
    },

    async obtenerNumeroClientes() {
      try {
        const clientes = await getClientesPorEmpresa(this.id_usuario);
        this.clientesNumero = clientes.totalClientes;
      } catch (error) {
        notis("error", "Error al buscar el total de clientes");
      } finally {
        this.isLoading = false;
      }
    },
    async getAlertasPromocion() {
      try {
        this.alertasPorPromocion = await getAlertasPorPromocion(
          this.id_usuario
        );
      } catch (error) {
        console.error(error);
        notis("error", "Error al mostras las promociones de productos");
      }
    },

    getAlertasPromocionFormatted() {
      try {
        if (this.alertasPorPromocion) {
          const { totalAlertas } = this.alertasPorPromocion;
          return totalAlertas;
        }
      } catch (error) {
        console.error(error);
        notis("error", "Error al mostras las promociones de productos");
      }
    },

    async getAlertasPorPromocionProducto() {
      try {
        this.alertasPorPromocionProducto = await getAlertasPorPromocionProducto(
          this.id_usuario
        );
      } catch (error) {
        console.error(error);
      }
    },

    getAlertasPorPromocionProductoFormatted() {
      if (this.alertasPorPromocionProducto) {
        const { totalAlertasProducto } = this.alertasPorPromocionProducto;
        return totalAlertasProducto;
      }
    },

    async obtenerVentasUltimosTresMeses() {
      try {
        const response = await getVentasUltimosTresMeses(this.id_usuario);
        if (response && response.chartData) {
          const apiLine = response.chartData.lineChartData;
          this.lineChartData = {
            labels: apiLine.labels,
            datasets: this.lineChartData.datasets.map((ds, i) => ({
              ...ds,
              data: apiLine.datasets[i]?.data ?? [],
            })),
          };
          this.lineChartOptions = {
            ...this.lineChartOptions,
            ...response.chartData.lineChartOptions,
          };
        }
      } catch (error) {
        console.error("Error al obtener ventas de los últimos 3 meses:", error);
      }
    },


    async obtenerUltimasVentas() {
      try {
        const response = await getUltimasVentas(this.id_usuario);
        if (response && response.sales) {
          this.sales = response.sales.map((venta) => ({
            ...venta,
            fecha: new Date(venta.fecha).toLocaleString("es-HN", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              hour12: false,
            }),
          }));
        }
      } catch (error) {
        console.error("Error al obtener últimas ventas:", error);
        if (!error?.message?.includes('401')) notis("error", "Error al obtener las últimas ventas");
      } finally {
        this.isLoading = false;
      }
    },

    async obtenerVentasComparativo() {
      try {
        const res = await getVentasComparativo(this.id_usuario);
        if (res) this.ventasComparativo = res;
      } catch (e) {
        console.error('Error ventas comparativo:', e);
      }
    },

    async obtenerCajaEstado() {
      try {
        const res = await getCajaEstado(this.id_usuario);
        if (res) this.cajaEstado = res;
      } catch (e) {
        console.error('Error caja estado:', e);
      }
    },

    async obtenerStockCritico() {
      try {
        const res = await getStockCritico(this.id_usuario);
        if (res) this.stockCritico = res.count || 0;
      } catch (e) {
        console.error('Error stock critico:', e);
      }
    },

    async obtenerTopProductos() {
      try {
        const res = await getTopProductos(this.id_usuario);
        if (Array.isArray(res)) this.topProductos = res;
      } catch (e) {
        console.error('Error top productos:', e);
      }
    },

    async obtenerVentasMes() {
      try {
        const res = await getVentasMes(this.id_usuario);
        if (res) this.ventasMes = res;
      } catch (e) {
        console.error('Error ventas mes:', e);
      }
    },

    async obtenerTopGeneral() {
      try {
        const res = await getTopProductosGeneralSucursal(this.id_usuario);
        if (Array.isArray(res)) this.topGeneral = res;
      } catch (e) {
        console.error('Error top general:', e);
      }
    },

    async obtenerCxCSucursal() {
      try {
        const res = await getTotalCxCSucursal(this.id_usuario);
        if (res) this.cxcSucursal = res;
      } catch (e) {
        console.error('Error CxC sucursal:', e);
      }
    },

    async refreshKpis() {
      // Verificar token antes de refrescar — si expiró, limpiar timer y redirigir
      try {
        await solicitudes.solicitarUsuarioToken();
      } catch {
        clearInterval(this.refreshTimer);
        return;
      }
      await Promise.all([
        this.devolverVenta(),
        this.obtenerVentasComparativo(),
        this.obtenerCajaEstado(),
        this.obtenerStockCritico(),
        this.obtenerTopProductos(),
        this.obtenerUltimasVentas(),
        this.obtenerDetalleAlertas(),
        this.obtenerVentasMes(),
        this.obtenerTopGeneral(),
        this.obtenerCxCSucursal(),
      ]);
    },
  },

  async mounted() {
    this.isLoading = true;
    try {
      this.id_usuario = await solicitudes.solicitarUsuarioToken();
      await Promise.all([
        this.devolverVenta(),
        this.obtenerVentasComparativo(),
        this.obtenerCajaEstado(),
        this.obtenerStockCritico(),
        this.obtenerTopProductos(),
        this.obtenerNumeroClientes(),
        this.getAlertasPromocion(),
        this.getAlertasPorPromocionProducto(),
        this.obtenerDetalleAlertas(),
        this.obtenerVentasUltimosTresMeses(),
        this.obtenerUltimasVentas(),
        this.obtenerVentasMes(),
        this.obtenerTopGeneral(),
        this.obtenerCxCSucursal(),
      ]);
      this.refreshTimer = setInterval(() => this.refreshKpis(), 60000);
    } catch (error) {
      console.error('Error inicializando dashboard:', error);
    } finally {
      this.isLoading = false;
    }
  },

  beforeUnmount() {
    if (this.refreshTimer) clearInterval(this.refreshTimer);
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap");

* {
  font-family: "Montserrat", sans-serif;
}

/* =======================================================
   Estilos Base del Dashboard
======================================================= */
.dashboard-container {
  padding: 20px;
  transition: all 0.3s ease;
}

/* Contenedor de las tarjetas */
.cards-container {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;
}

/* Contenedor de los gráficos */
.graphics-container {
  padding: 0;
}

/* Estilo para las tarjetas */
.card {
  background-color: #ffffff;
  padding: 1.25rem;
  border-radius: 8px;
  text-align: center;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.card-content {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
}

.card-icon {
  margin-top: 0.75rem;
  font-size: 1.6rem;
  color: #333;
}

.item-value {
  display: inline;
  padding: 6px;
  color: #333;
}

.card h3 {
  font-size: 1.05rem;
  color: #333;
  margin-bottom: 0.4rem;
}

.card p {
  font-size: 1.4rem;
  font-weight: bold;
  color: #333;
  margin: 0.25rem 0;
}

.card-link {
  display: block;
  margin-top: 0.75rem;
  color: #007bff;
  text-decoration: none;
  font-weight: bold;
  transition: color 0.3s ease;
}

/* Contenedor de los gráficos */
.charts-container {
  display: flex;
  gap: 1.25rem;
  margin-bottom: 1.5rem;
  height: 280px;
}

.line-chart,
.top-panel {
  position: relative;
  flex: 1;
  min-width: 0;
  background-color: #ffffff;
  padding: 1rem;
  border-radius: 8px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.top-panel-header {
  font-size: 0.88rem;
  font-weight: 700;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 0.75rem;
  padding-bottom: 0.4rem;
  border-bottom: 1px solid #f3f4f6;
}

.top-col-label {
  margin-left: auto;
  font-size: 0.72rem;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.top-panel-list {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.top-panel-item {
  display: grid;
  grid-template-columns: 28px 1fr auto;
  align-items: center;
  gap: 8px;
  padding: 5px 6px;
  border-radius: 5px;
  background: #f9fafb;
  font-size: 0.82rem;
}

/* Wrapper scrollable para tabla de ventas */
.sales-table-wrap {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border-radius: 8px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

/* Estilo para la tabla de ventas */
.sales-table {
  width: 100%;
  min-width: 520px;
  font-size: 0.9rem;
  border-collapse: collapse;
  margin-top: 1.25rem;
  background-color: #ffffff;
  border-radius: 8px;
  overflow: hidden;
}

.sales-table th,
.sales-table td {
  padding: 8px 10px;
  border: 1px solid #ddd;
  transition: all 0.3s ease;
}

.sales-table th {
  background-color: #f4f4f4;
  font-weight: bold;
  font-size: 0.85rem;
  color: #333;
}

.sales-table td {
  text-align: center;
  color: #333;
}

.sales-table a,
.sales-table button {
  margin-right: 10px;
  text-decoration: none;
  color: #007bff;
  border: none;
  background-color: transparent;
  cursor: pointer;
  transition: color 0.3s ease;
}

.sales-table span {
  color: #ddd;
  margin: 0 5px;
}

/* =======================================================
   Estilos en Modo Oscuro
======================================================= */
/* Contenedor principal en modo oscuro */
.dark .dashboard-container {
  background-color: #1e1e1e;
  color: #fff;
}

/* Tarjetas en modo oscuro */
.dark .card {
  background-color: #2d2d2d;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
}

.dark .card h3,
.dark .card p,
.dark .item-value {
  color: #fff;
}

.dark .card-icon {
  color: #c09d62;
}

.dark .card-link {
  color: #c09d62;
}

.dark .card-link:hover {
  color: #a38655;
}

/* Gráficos en modo oscuro */
.dark .line-chart,
.dark .top-panel {
  background-color: #2d2d2d;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
}

.dark .top-panel-header { color: #e5e7eb; border-bottom-color: #383838; }
.dark .top-panel-item { background: #383838; }
.dark .top-nombre { color: #f3f4f6; }
.dark .top-unidades { color: #9ca3af; }

/* Tabla en modo oscuro */
.dark .sales-table {
  background-color: #2d2d2d;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
}

.dark .sales-table th {
  background-color: #383838;
  color: #fff;
  border-color: #404040;
}

.dark .sales-table td {
  color: #fff;
  border-color: #404040;
}

.dark .sales-table tr:hover {
  background-color: #383838;
}

.dark .sales-table a,
.dark .sales-table button {
  color: #c09d62;
}

.dark .sales-table a:hover,
.dark .sales-table button:hover {
  color: #a38655;
}

.dark .sales-table span {
  color: #666;
}

/* Personalización de los gráficos en modo oscuro */
.dark .charts-container canvas {
  background-color: #2d2d2d;
}

/* =======================================================
   Estilos Responsivos
======================================================= */

/* Laptops medianas */
@media (max-width: 1400px) {
  .cards-container {
    grid-template-columns: repeat(3, 1fr);
    gap: 0.8rem;
  }
}

@media (max-width: 1200px) {
  .cards-container {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.8rem;
  }
  .card { padding: 1rem; }
  .card h3 { font-size: 0.95rem; }
  .card-icon { font-size: 1.4rem; }
}

/* Tablets — fix contenedor de gráficos (280px fijo → auto) */
@media (max-width: 900px) {
  .charts-container {
    height: auto;
  }
  .line-chart { height: 240px; }
  .top-panel  { height: auto; }
}

@media (max-width: 768px) {
  .dashboard-container { padding: 12px; }
  .cards-container {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.65rem;
    margin-bottom: 1.25rem;
  }
  .card { padding: 1rem; }
  .card h3 { font-size: 0.95rem; }
  .item-value { font-size: 1.1rem; padding: 5px; }
  .card-icon { margin-top: 0.75rem; }

  .charts-container {
    flex-direction: column;
    height: auto;
    gap: 0.75rem;
  }
  .line-chart { height: 220px; }
  .top-panel  { height: auto; }

  .sales-table th,
  .sales-table td {
    padding: 8px 7px;
    font-size: 0.85rem;
  }
}

/* Móviles */
@media (max-width: 480px) {
  .dashboard-container { padding: 10px; }

  .cards-container {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.45rem;
    margin-bottom: 1rem;
  }
  .card { padding: 0.7rem 0.6rem; }
  .card h3 {
    font-size: 0.8rem;
    margin-bottom: 0.2rem;
  }
  .card p { font-size: 0.95rem; }
  .item-value { font-size: 0.88rem; padding: 3px; }
  .card-icon  { font-size: 1.1rem; margin-top: 0.4rem; }
  .card-link  { font-size: 0.73rem; margin-top: 0.35rem; }
  .trend-badge { font-size: 0.68rem; padding: 1px 5px; }
  .caja-badge  { font-size: 0.78rem; padding: 2px 8px; }
  .caja-monto  { font-size: 0.75rem; }
  .cxc-sub     { font-size: 0.7rem; }

  /* Alert boxes más compactos */
  .alert-boxes { gap: 5px; margin-top: 0.6rem; }
  .alert-box   { min-width: 46px; padding: 5px 3px; }
  .alert-box span { font-size: 0.95rem; }
  .alert-box small { font-size: 0.68rem; }
  .stock-critico-badge { font-size: 0.68rem; padding: 2px 6px; }

  .charts-container { gap: 0.6rem; }
  .line-chart { height: 195px; }
  .line-chart,
  .top-panel  { padding: 0.65rem; }
  .top-panel-header { font-size: 0.8rem; margin-bottom: 0.5rem; }
  .top-panel-item   { font-size: 0.76rem; padding: 4px 4px; gap: 6px; }
  .top-col-label    { font-size: 0.66rem; }

  .sales-table th,
  .sales-table td {
    padding: 7px 5px;
    font-size: 0.78rem;
  }
}

/* Móviles muy pequeños */
@media (max-width: 360px) {
  .cards-container {
    grid-template-columns: 1fr;
    gap: 0.4rem;
  }
  .card { padding: 0.65rem; }
  .card h3 { font-size: 0.78rem; }
  .item-value { font-size: 0.82rem; }
  .card-icon  { font-size: 1rem; }
  .card-link  { font-size: 0.7rem; }
  .line-chart,
  .top-panel  { padding: 0.5rem; }
}

/* Landscape en móvil */
@media (max-height: 480px) and (orientation: landscape) {
  .dashboard-container { padding: 8px; }
  .cards-container {
    grid-template-columns: repeat(5, 1fr);
    gap: 0.4rem;
    margin-bottom: 0.75rem;
  }
  .card { padding: 0.55rem; }
  .card h3 { font-size: 0.75rem; }
  .card-icon { margin-top: 0.3rem; font-size: 1rem; }
  .charts-container {
    flex-direction: row;
    height: 200px;
  }
  .line-chart,
  .top-panel { width: 50%; }
}

.no-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #aaa;
  gap: 0.5rem;
}

.no-data i {
  font-size: 2.5rem;
}

.no-data p {
  font-size: 0.9rem;
  font-weight: normal;
  color: #aaa;
  margin: 0;
}

.alert-boxes {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 1rem;
}

.alert-box {
  padding: 8px;
  border-radius: 6px;
  min-width: 60px;
  text-align: center;
}

.alert-box span {
  display: block;
  font-size: 1.2rem;
  font-weight: bold;
}

.alert-box small {
  font-size: 0.75rem;
}

.alert-box.normal {
  background-color: #eafaf1;
  border: 2px solid #2ecc71;
}

.alert-box.warning {
  background-color: #fff8e6;
  border: 2px solid #f39c12;
}

.alert-box.urgent {
  background-color: #fdecea;
  border: 2px solid #e74c3c;
}

/* Dark mode */
.dark .alert-box.normal {
  background-color: rgba(46, 204, 113, 0.2);
}

.dark .alert-box.warning {
  background-color: rgba(243, 156, 18, 0.2);
}

.dark .alert-box.urgent {
  background-color: rgba(231, 76, 60, 0.2);
}

/* ── Trend badge (ventas comparativo) ─────────────────── */
.trend-badge {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 12px;
  margin-top: 4px;
}

.trend-up   { background: #d1fae5; color: #065f46; }
.trend-down { background: #fee2e2; color: #991b1b; }

.dark .trend-up   { background: rgba(16,185,129,0.2); color: #6ee7b7; }
.dark .trend-down { background: rgba(220,38,38,0.2);  color: #fca5a5; }

/* ── Caja badge ────────────────────────────────────────── */
.caja-badge {
  display: inline-block;
  padding: 3px 12px;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 700;
  margin-top: 6px;
}

.badge-green { background: #d1fae5; color: #065f46; }
.badge-red   { background: #fee2e2; color: #991b1b; }
.badge-grey  { background: #f3f4f6; color: #6b7280; }

.dark .badge-green { background: rgba(16,185,129,0.2); color: #6ee7b7; }
.dark .badge-red   { background: rgba(220,38,38,0.2);  color: #fca5a5; }
.dark .badge-grey  { background: rgba(107,114,128,0.2); color: #9ca3af; }

.caja-monto {
  font-size: 0.82rem;
  color: #6b7280;
  margin: 2px 0 0;
  font-weight: 500;
}

.dark .caja-monto { color: #9ca3af; }

/* ── Stock crítico badge dentro de alertas card ────────── */
.stock-critico-badge {
  margin-top: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #b45309;
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  padding: 3px 8px;
  display: inline-block;
}

.dark .stock-critico-badge {
  background: rgba(245,158,11,0.15);
  color: #fcd34d;
  border-color: #d97706;
}

/* ── CxC sub-label ─────────────────────────────────────── */
.cxc-sub {
  font-size: 0.75rem;
  color: #6b7280;
  display: block;
  margin-top: 2px;
}
.dark .cxc-sub { color: #9ca3af; }

/* ── Rank medals ────────────────────────────────────────── */
.rank-gold   { color: #D97706 !important; }
.rank-silver { color: #6b7280 !important; }
.rank-bronze { color: #92400e !important; }

/* ── Top Productos section ─────────────────────────────── */
.top-productos-section {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
  padding: 1rem 1.25rem;
  margin-bottom: 1.5rem;
}

.dark .top-productos-section {
  background: #2d2d2d;
  box-shadow: 0 2px 5px rgba(0,0,0,0.3);
}

.section-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #374151;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 6px;
}

.dark .section-title { color: #e5e7eb; }

.top-productos-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.top-producto-item {
  display: grid;
  grid-template-columns: 28px 1fr auto;
  align-items: center;
  gap: 10px;
  padding: 6px 8px;
  border-radius: 6px;
  background: #f9fafb;
  font-size: 0.87rem;
}

.dark .top-producto-item { background: #383838; }

.top-rank {
  font-weight: 700;
  color: #6b7280;
  font-size: 0.8rem;
  font-variant-numeric: tabular-nums;
}

.top-nombre {
  font-weight: 500;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dark .top-nombre { color: #f3f4f6; }

.top-unidades {
  font-size: 0.78rem;
  color: #6b7280;
  white-space: nowrap;
}

.dark .top-unidades { color: #9ca3af; }

</style>
